/**
 * GamePlayerBar — the compact player strip pinned to the top of the game LOBBIES: Play,
 * Campaign, Trial, Ranked, Squad Ranked and the Lab (see GameShell's PLAYER_BAR_PAGES).
 * Never inside a live battle — the arena stays undistracted — and never on content pages.
 *
 * It answers the three things a player needs at a glance between runs, in one row:
 *   • who am I  → trainer picture + name (tap → profile)
 *   • what can I spend → pooled SOUL SHARDS and Trainer SUIKA
 *   • how do I act on it → a "+" that opens the buy-Suika-for-the-trainer dialog
 *
 * **SOUL SHARDS ARE A PLAYER'S, NOT A SUIKAMON'S.** The balance is pooled across every pet and
 * every linked wallet, and the daily ladder that fills it is account-keyed — so the only honest
 * home for it is this player-level strip. It used to be reachable ONLY from a 🎁 button inside a
 * single pet's care panel, which told every player the opposite: that today's rewards were that
 * one Suikamon's. The chip leads to the Skills shop, where they are spent.
 *
 * Deliberately self-contained (no props but presentation ones), so adding it to a screen is a
 * one-line change with no prop plumbing. The profile is read through the SHARED React Query
 * key (`trainerProfileQueryKey`), so mounting this on every lobby costs no extra fetch — it
 * reads the same cache entry as the rail avatar and the Play page.
 *
 * Identity gating follows CLAUDE.md: the bar keys on the authenticated IDENTITY via
 * `useWalletConnection()` — never on a Sui browser wallet — so EVM/Solana/zkLogin players see
 * it too.
 *
 * **THE SUIKA CHIP IS THE PLAYER'S WALLET** — the one Suika balance every Suikamon spends from
 * and earns into (`usePlayerSuika`; per-pet pockets are gone since 2026-09-28). Guests have a
 * wallet too. Below the free-refill threshold the chip becomes the way to refill it.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePlayerSuika } from "../../hooks/usePlayerSuika";
import { FreeSuikaRefillDialog } from "../shared/FreeSuikaRefillDialog";
import { useNavigate } from "react-router-dom";
import { isGuestOrNoWallet } from "@shared/domain/guestSession";
import { useWalletConnection } from "../../auth/useWalletConnection";
import { fetchOwnerPets, fetchTrainerProfile } from "../../api/petBackendApi";
import { ownerPetsQueryKey, trainerProfileQueryKey } from "../../hooks/useTrainerProfile";
import { TrainerAvatar } from "../TrainerAvatar";
import { CurrencyIcon } from "../shared/CurrencyIcon";
import { useT } from "../../i18n/i18nContext";
import "./GamePlayerBar.css";

/** Avatar diameter in the bar — sized to the 40px-tall strip, not the 88px hero card. */
const BAR_AVATAR_SIZE = 26;
/** Matches the shared profile query's staleTime so the bar never triggers its own refetch. */
const PROFILE_STALE_TIME_MS = 30_000;
/** Fallback name before a player sets one (same wording as the trainer hero card). */
const DEFAULT_TRAINER_NAME = "Trainer";
/** Same staleTime as the other `ownerPets` readers, so the shared cache entry agrees. */
const PETS_STALE_TIME_MS = 30_000;
/** Label for a pre-identity free-play player. */
const GUEST_TRAINER_NAME = "Guest";

/**
 * Scrolled less than this, the bar floats over a screen's stage with no glass of its own (only
 * its chips show over the scene); past it the glass comes back so the chips stay readable over
 * the cards that scroll under it. GamePlayerBar.css scopes the glassless look to stage screens.
 */
const BAR_GLASS_SCROLL_PX = 24;

/** Whether the page is still at its top, kept in step with the window's scroll. */
function useAtPageTop(threshold: number): boolean {
  const [atTop, setAtTop] = useState(() => typeof window === "undefined" || window.scrollY < threshold);
  useEffect(() => {
    const update = () => setAtTop(window.scrollY < threshold);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [threshold]);
  return atTop;
}

interface GamePlayerBarProps {
  /** Extra class for screen-specific placement (e.g. the battle arena's overlay variant). */
  className?: string;
}

export function GamePlayerBar({ className }: GamePlayerBarProps) {
  const navigate = useNavigate();
  const t = useT();
  const { actingAddress } = useWalletConnection();
  const isGuest = isGuestOrNoWallet(actingAddress);
  const wallet = usePlayerSuika();
  const [refillOpen, setRefillOpen] = useState(false);
  const atTop = useAtPageTop(BAR_GLASS_SCROLL_PX);

  const { data: profile } = useQuery({
    queryKey: trainerProfileQueryKey(actingAddress),
    queryFn: () => fetchTrainerProfile(actingAddress as string),
    enabled: !!actingAddress && !isGuest,
    staleTime: PROFILE_STALE_TIME_MS,
  });

  // Same key and fetcher as the profile and referrals pages: one roster request, shared.
  const { data: pets } = useQuery({
    queryKey: ownerPetsQueryKey(actingAddress),
    queryFn: () => fetchOwnerPets(actingAddress as string),
    enabled: !!actingAddress,
    staleTime: PETS_STALE_TIME_MS,
  });

  // Signed out entirely: nothing to identify, so no bar (the page's own sign-in CTA leads).
  if (!actingAddress) return null;

  const displayName = isGuest
    ? GUEST_TRAINER_NAME
    : profile?.displayName || DEFAULT_TRAINER_NAME;
  const suikaBalance = wallet.balance;
  const refillPetId = pets?.[0]?.pet.id;

  return (
    <div className={`game-player-bar${atTop ? " game-player-bar--at-top" : ""}${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="game-player-bar__identity"
        onClick={() => navigate("/profile")}
        aria-label={t("navigation.game.openProfile", { name: displayName })}
      >
        <TrainerAvatar
          avatar={profile?.avatar ?? null}
          size={BAR_AVATAR_SIZE}
          className="game-player-bar__avatar"
        />
        <span className="game-player-bar__name">{displayName}</span>
      </button>

      <div className="game-player-bar__wallet">
        {/* The player's Suika wallet (see the header comment). Low: tap for the free refill. */}
        {wallet.isLow && refillPetId ? (
          <button
            type="button"
            className="game-player-bar__suika game-player-bar__suika--low"
            onClick={() => setRefillOpen(true)}
            aria-label={t("navigation.game.suikaBalanceLow", { amount: suikaBalance })}
          >
            <CurrencyIcon currency="suika" />
            <span className="game-player-bar__suika-value">{suikaBalance}</span>
          </button>
        ) : (
          <span className="game-player-bar__suika" aria-label={t("navigation.game.suikaBalance", { amount: suikaBalance })}>
            <CurrencyIcon currency="suika" />
            <span className="game-player-bar__suika-value">{suikaBalance}</span>
          </span>
        )}
      </div>

      {refillPetId && (
        <FreeSuikaRefillDialog
          open={refillOpen}
          onClose={() => setRefillOpen(false)}
          petId={refillPetId}
          balance={suikaBalance}
          onRefilled={wallet.refresh}
        />
      )}
    </div>
  );
}
